import _ from 'lodash';
import { EditorAPIKey } from '#packages/apis';
import constants from '#packages/constants';
import { designData } from '#packages/util';
import * as columnsUtils from './columnsWrapperUtils';
import { sendAddColumnBI } from './columnsBi';
import { components, interactions } from '#packages/stateManagement';
//eslint-disable-next-line @wix/santa-editor/scoped-imports
import * as serializedComponentPlugins from '@/rEditor/app/serializedComponentPlugins';

import type { CompLayoutUpdate, CompRef, Rect } from 'types/documentServices';
import type { Shell } from '#packages/apilib';
import type { ComponentsAddResult } from '#packages/components';

const componentsSelectors = components.selectors;

const { hasDividersDesign } = designData;

const { transferStripInteractionToSingleColumn } = interactions.actions;

const { isInteractionModeAvailable } = interactions.selectors;

export function createColumnsApi(shell: Shell) {
  const editorAPI = shell.getAPI(EditorAPIKey);
  const MANAGE_COLUMNS_PANEL_NAME =
    'compPanels.panels.StripColumnsContainer.managePanel';
  const MAX_COLUMNS = 5;
  const FIVE_COLUMNS_PROPORTIONS = Array(MAX_COLUMNS).fill(1);

  const ALIGNMENT = {
    LEFT: 0,
    CENTER: 50,
    RIGHT: 100,
  };

  const COMPS_THAT_HANDLE_CHILDREN_RESIZE = {
    BoxSlideShow: true,
    StripContainerSlideShow: true,
  };

  let isDuplicateLocked = false;

  const initialColumnChildrenLayoutsMap = new Map<string, Rect>();
  let initialProportions: number[] = [];

  function setInitialColumnChildrenLayouts(columnsContainerPointer: CompRef) {
    initialProportions = getProportions(columnsContainerPointer);

    const columnsDescendants = editorAPI.components
      .getChildren(columnsContainerPointer)
      .map((column) => {
        initialColumnChildrenLayoutsMap.set(
          column.id,
          editorAPI.components.layout.get_rect(column),
        );
        return column;
      })
      .flatMap((child) => getCompDescendants(child));
    columnsDescendants.forEach((descendant) => {
      initialColumnChildrenLayoutsMap.set(
        descendant.id,
        editorAPI.components.layout.get_rect(descendant),
      );
    });
  }

  function clearInitialColumnChildrenLayouts() {
    initialColumnChildrenLayoutsMap.clear();
  }

  function setProportions(
    columnsContainerPointer: CompRef,
    proportionItem: number[],
    dontAddToUndoStack?: boolean,
  ) {
    const previousProportion = getProportions(columnsContainerPointer);
    const children = editorAPI.components.getChildren(columnsContainerPointer);
    const dontAddToUndoRedoStack = true;
    proportionItem = columnsUtils.get980Proportions(proportionItem);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
    _.forEach(children, function (child, index) {
      // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
      editorAPI.components.layout.update(
        child,
        { width: proportionItem[index] },
        dontAddToUndoRedoStack,
      );
    });
    const currentProportions = columnsUtils.toPercentages(proportionItem);
    setChildrenLayoutAccordingToProportion(
      children,
      previousProportion,
      currentProportions,
    );

    if (!dontAddToUndoStack) {
      editorAPI.history.add('Change Columns Proportions');
    }
  }

  function getProportions(columnsContainerPointer: CompRef) {
    const children = editorAPI.components.getChildren(columnsContainerPointer);
    const widths = children.map(
      (child: CompRef) => editorAPI.components.layout.get_size(child).width,
    );

    return columnsUtils.toPercentages(widths);
  }

  function resetProportionsIfNeeded(
    columnsContainerPointer: CompRef,
    dontAddToUndoStack: boolean,
  ) {
    if (
      editorAPI.components.getChildren(columnsContainerPointer).length ===
      MAX_COLUMNS
    ) {
      setProportions(
        columnsContainerPointer,
        FIVE_COLUMNS_PROPORTIONS,
        dontAddToUndoStack,
      );
    }
  }

  function flipProportions(columnsContainerPointer: CompRef) {
    const currentProportions = getProportions(columnsContainerPointer);
    const reveresProportions = currentProportions.reverse();
    setProportions(columnsContainerPointer, reveresProportions);
  }

  function duplicateColumn(columnPointer: CompRef, origin: string) {
    const serializeWithOriginalLanguage = _.partialRight(
      editorAPI.components.serialize,
      null,
      null,
      null,
      null,
      null,
      true,
    );

    let columnStructure = serializeWithOriginalLanguage(columnPointer);

    columnStructure = columnsUtils.removeFullWidthChildrenFromStructure(
      editorAPI,
      columnStructure,
    );
    columnStructure =
      columnsUtils.removeNonDuplicatableComponentsFromStructure(
        columnStructure,
      );

    const columnsContainerPointer =
      editorAPI.components.getContainer(columnPointer);
    const columns = editorAPI.components.getChildren(columnsContainerPointer);

    if (columns.length === MAX_COLUMNS || isDuplicateLocked) {
      return;
    }

    isDuplicateLocked = true;

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/find-index
    const index = _.findIndex(columns, columnPointer) + 1;
    const previousProportion = getProportions(columnsContainerPointer);
    previousProportion.splice(index, 0, previousProportion[index - 1]);
    const currentProportion = columnsUtils.toPercentages(previousProportion);

    columnStructure =
      serializedComponentPlugins.runCopyPluginOnSerializedComponent(
        columnStructure,
        columnPointer,
        editorAPI,
      );
    columnStructure =
      serializedComponentPlugins.runPastePluginOnSerializedComponent(
        columnStructure,
        editorAPI,
      );

    const compRefOrAddResult: ComponentsAddResult = editorAPI.components.add(
      columnsContainerPointer,
      columnStructure,
    );

    const onAppliedInner = async (duplicatedRef: CompRef) => {
      const dontAddToUndoRedoStack = true;
      columnsUtils.moveToIndex(
        editorAPI,
        duplicatedRef,
        index,
        dontAddToUndoRedoStack,
      );
      resetProportionsIfNeeded(columnsContainerPointer, true);

      const columnsAfterAdd = editorAPI.components.getChildren(
        columnsContainerPointer,
      );

      setChildrenLayoutAccordingToProportion(
        columnsAfterAdd,
        previousProportion,
        currentProportion,
      );
      isDuplicateLocked = false;

      await editorAPI.dsActions.waitForChangesAppliedAsync();

      editorAPI.history.add('added column', { isAddingComponent: true });
      sendAddColumnBI(editorAPI, duplicatedRef, origin);

      return duplicatedRef;
    };

    if (compRefOrAddResult && 'hooks' in compRefOrAddResult) {
      const { hooks } = compRefOrAddResult;

      return {
        hooks: {
          waitForCompRef: hooks.waitForCompRef,
          waitForChangesApplied:
            hooks.waitForChangesApplied.then(onAppliedInner),
        },
      };
    }

    editorAPI.dsActions.waitForChangesApplied(() => {
      onAppliedInner(compRefOrAddResult as CompRef);
    });

    return compRefOrAddResult as CompRef;
  }

  function addColumn(columnsContainerPointer: CompRef, origin: string) {
    const columns = editorAPI.components.getChildren(columnsContainerPointer);
    const lastColumn = columns[columns.length - 1];

    if (columns.length === MAX_COLUMNS) {
      return;
    }

    return duplicateColumn(lastColumn, origin);
  }

  function setColumnAlignment(columnPointer: CompRef, alignment: number) {
    editorAPI.components.properties.update(columnPointer, { alignment });
  }

  function getColumnAlignment(columnPointer: CompRef) {
    return editorAPI.components.properties.get(columnPointer).alignment;
  }

  function moveLeft(columnPointer: CompRef) {
    editorAPI.components.arrangement.moveBackward(columnPointer);
  }

  function moveRight(columnPointer: CompRef) {
    editorAPI.components.arrangement.moveForward(columnPointer);
  }

  function canAdd(columnsContainerPointer: CompRef) {
    return (
      editorAPI.components.getChildren(columnsContainerPointer).length <
      MAX_COLUMNS
    );
  }

  function isCompHandlingItsOwnChildrenResize(comp: CompRef) {
    const compType = componentsSelectors.getCompTypeSuffix(
      comp,
      editorAPI.dsRead,
    );
    return !!COMPS_THAT_HANDLE_CHILDREN_RESIZE[
      compType as keyof typeof COMPS_THAT_HANDLE_CHILDREN_RESIZE
    ];
  }

  function getCompDescendants(comp: CompRef): CompRef[] {
    const children =
      editorAPI.components.getChildren_DEPRECATED_BAD_PERFORMANCE(comp);

    return _(children)
      .reject(isCompHandlingItsOwnChildrenResize)
      .flatMap(getCompDescendants)
      .union(children)
      .value() as any as CompRef[];
  }

  function resizeCompVerticallyIfApplicable(
    columnRef: CompRef,
    compRef: CompRef,
    ratio: number,
  ) {
    const compParent = editorAPI.components.getContainerOrScopeOwner(compRef);
    const compIsDirectColumnChild = editorAPI.columns.isColumn(compParent);

    if (!compIsDirectColumnChild) {
      return;
    }

    const compLayoutBeforeResize = initialColumnChildrenLayoutsMap.get(
      compRef.id,
    );
    const columnLayoutBeforeResize = initialColumnChildrenLayoutsMap.get(
      columnRef.id,
    );

    const layoutChanges: CompLayoutUpdate = {
      width: compLayoutBeforeResize.width,
    };

    const newColumnWidth = columnLayoutBeforeResize.width * ratio;
    const columnWidthsDiff = newColumnWidth - columnLayoutBeforeResize.width;
    layoutChanges.x = compLayoutBeforeResize.x + columnWidthsDiff / 2;

    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.components.layout.update(compRef, layoutChanges, true);

    updateCompChildrenLayoutRecursively(compRef);
  }

  const updateCompChildrenLayoutRecursively = (compParent: CompRef) => {
    const compChildren = editorAPI.components.getChildren(compParent);
    if (compChildren.length) {
      compChildren.forEach((child) => {
        // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
        editorAPI.components.layout.update(
          child,
          initialColumnChildrenLayoutsMap.get(child.id),
        );
        updateCompChildrenLayoutRecursively(child);
      });
    }
  };

  function resizeCompVerticallyByRatio(
    columnLayoutRelativeToStructure: Rect,
    compRef: CompRef,
    ratio: number,
    alignment: number,
    column?: CompRef,
    ratioRelativeToInitialProportions?: number,
  ) {
    if (editorAPI.components.is.group(compRef)) {
      return;
    }
    const shouldKeepChildrenProportionsOnColumnResize =
      column &&
      ratioRelativeToInitialProportions &&
      !!initialColumnChildrenLayoutsMap.size;
    if (shouldKeepChildrenProportionsOnColumnResize) {
      if (+ratioRelativeToInitialProportions.toFixed(1) >= 1) {
        resizeCompVerticallyIfApplicable(
          column,
          compRef,
          ratioRelativeToInitialProportions,
        );
        return;
      }
    }

    //TODO: consider margin and containers
    const compLayoutRelativeToStructure =
      editorAPI.components.layout.getRelativeToStructure(compRef);
    const compXRelativeToColumn =
      compLayoutRelativeToStructure.x - columnLayoutRelativeToStructure.x;
    const originalWidth = compLayoutRelativeToStructure.width;
    const isResizable = editorAPI.components.is.resizable(compRef);

    const currentWidth = isResizable ? originalWidth * ratio : originalWidth;
    const layoutChanges: CompLayoutUpdate = {
      width: currentWidth,
    };

    if (alignment === ALIGNMENT.LEFT) {
      layoutChanges.x = compXRelativeToColumn * ratio;
    } else if (alignment === ALIGNMENT.CENTER) {
      const originalCenter = compXRelativeToColumn + originalWidth / 2;
      const currentCenter = originalCenter * ratio;
      layoutChanges.x = currentCenter - currentWidth / 2;
    } else {
      const originalRight = compXRelativeToColumn + originalWidth;
      const currentRight = originalRight * ratio;
      layoutChanges.x = currentRight - currentWidth;
    }

    layoutChanges.x += columnLayoutRelativeToStructure.x;

    const dontAddToUndoRedoStack = true;
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.components.layout.updateRelativeToStructure(
      compRef,
      layoutChanges,
      dontAddToUndoRedoStack,
    );
  }

  function setChildrenLayoutAccordingToProportion(
    columns: CompRef[],
    previousProportion: number[],
    currentProportion: number[],
  ) {
    columns.forEach((column, index) => {
      const resizeRatio = columnsUtils.getColumnResizeRatio(
        currentProportion,
        previousProportion,
        index,
      );

      if (resizeRatio) {
        const columnDescendants = getCompDescendants(column);
        if (columnDescendants.length === 0) {
          return;
        }

        const { alignment } = editorAPI.components.properties.get(column);
        const columnLayoutRelativeToStructure =
          editorAPI.components.layout.getRelativeToStructure(column);
        const ratioRelativeToInitialProportions =
          initialProportions.length &&
          columnsUtils.getColumnResizeRatio(
            currentProportion,
            initialProportions,
            index,
          );

        columnDescendants.forEach((descendant) => {
          resizeCompVerticallyByRatio(
            columnLayoutRelativeToStructure,
            descendant,
            resizeRatio,
            alignment,
            column,
            ratioRelativeToInitialProportions,
          );
        });
      }
    });
  }

  type ProportionsPreset = number[];

  const equalColumnsLayouts: Record<string, ProportionsPreset> = {
    '2': [1, 1],
    '3': [1, 1, 1],
    '4': [1, 1, 1, 1],
  };

  function checkEqualColumnsLayout(columnsContainerPointer: CompRef) {
    const currentProportions = getProportions(columnsContainerPointer);
    const layoutPreset = equalColumnsLayouts[currentProportions.length];

    if (!layoutPreset) {
      return false;
    }

    const layoutProportions = columnsUtils.toPercentages(layoutPreset);
    const normalizedLayoutProportions =
      columnsUtils.toProportionsIntegers(layoutProportions);
    return normalizedLayoutProportions.every(function (v, i) {
      return Math.abs(Math.floor(v) - currentProportions[i]) < 0.5;
    });
  }

  function applyEqualColumnsLayout(
    columnsContainerPointer: CompRef,
    columnsCount: number,
  ) {
    const newLayoutPreset = equalColumnsLayouts[columnsCount];

    if (newLayoutPreset) {
      const newProportions = columnsUtils.toProportionsIntegers(
        columnsUtils.toPercentages(newLayoutPreset),
      );
      const doNotAddToUndoStack = true;

      setProportions(
        columnsContainerPointer,
        newProportions,
        doNotAddToUndoStack,
      );
    }
  }

  async function removeColumn(
    compPtr: CompRef,
    allowRemoveMessage: boolean,
    originRemove: VoidFunction,
  ) {
    const removeFunction =
      originRemove || _.partial(editorAPI.dsActions.components.remove, compPtr);
    const columnsContainerPointer = editorAPI.components.getContainer(compPtr);
    const columns = editorAPI.components.getChildren(columnsContainerPointer);
    const hasEqualColumnsLayoutBeforeRemove = checkEqualColumnsLayout(
      columnsContainerPointer,
    );

    const oldColumnsWidthProportionsMap =
      columnsUtils.getColumnsWidthProportionsMap(
        editorAPI,
        columnsContainerPointer,
      );

    removeFunction();

    return new Promise<void>((resolve) => {
      editorAPI.dsActions.waitForChangesApplied(function () {
        const newColumnsWidthProportionsMap =
          columnsUtils.getColumnsWidthProportionsMap(
            editorAPI,
            columnsContainerPointer,
          );

        if (hasEqualColumnsLayoutBeforeRemove) {
          const columnsCount = Object.keys(
            newColumnsWidthProportionsMap,
          ).length;
          applyEqualColumnsLayout(columnsContainerPointer, columnsCount);
        }
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
        Object.entries(newColumnsWidthProportionsMap).forEach(
          ([columnId, widthProportion]) => {
            const resizeRatio =
              widthProportion / oldColumnsWidthProportionsMap[columnId];
            const column = columns.find((column) => column.id === columnId);

            const columnDescendants = getCompDescendants(column);
            const { alignment } = editorAPI.components.properties.get(column);
            const columnLayoutRelativeToStructure =
              editorAPI.components.layout.getRelativeToStructure(column);

            columnDescendants.forEach((descendant) => {
              resizeCompVerticallyByRatio(
                columnLayoutRelativeToStructure,
                descendant,
                resizeRatio,
                alignment,
              );
            });
          },
        );

        if (
          Object.keys(newColumnsWidthProportionsMap).length === 1 &&
          !hasDividersDesign(editorAPI, columnsContainerPointer)
        ) {
          editorAPI.components.design.update(
            columnsContainerPointer,
            constants.COMPONENT_DESIGN.EMPTY_BACKGROUND,
            undefined,
            true,
          );
        }

        if (
          isStripWithSingleColumn(columnsContainerPointer) &&
          hasDividersDesign(editorAPI, columnsContainerPointer)
        ) {
          editorAPI.components.design.update(
            editorAPI.components.getChildren(columnsContainerPointer)[0],
            constants.COMPONENT_DESIGN.EMPTY_BACKGROUND,
            undefined,
            true,
          );
        }

        columnsUtils.resetColumnStyleIfNeeded(
          editorAPI,
          columnsContainerPointer,
        );
        columnsUtils.resetStripStyleIfNeeded(
          editorAPI,
          columnsContainerPointer,
        );

        if (
          isStripWithSingleColumn(columnsContainerPointer) &&
          isInteractionModeAvailable(editorAPI.dsRead)
        ) {
          transferStripInteractionToSingleColumn(
            editorAPI,
            columnsContainerPointer,
          );
          editorAPI.dsActions.waitForChangesApplied(() => {
            editorAPI.selection.selectComponentByCompRef(
              columnsContainerPointer,
            );
            resolve();
          });
          return;
        }
        editorAPI.selection.selectComponentByCompRef(columnsContainerPointer);
        resolve();
      });
    });
  }

  function isStripWithSingleColumn(columnsContainerPtr: CompRef | CompRef[]) {
    if (!isStrip(columnsContainerPtr)) {
      return false;
    }

    const columns = editorAPI.components.getChildren(columnsContainerPtr);
    return columns.length === 1;
  }

  function getColumnIfStripIsSingleColumn(stripRef: CompRef): CompRef {
    const columns = editorAPI.components.getChildren(stripRef);
    if (columns.length === 1) {
      return columns[0];
    }
    return stripRef;
  }

  function isStripWithMultipleColumns(
    columnsContainerPtr: CompRef | CompRef[],
  ) {
    if (!isStrip(columnsContainerPtr)) {
      return false;
    }

    return !isStripWithSingleColumn(columnsContainerPtr);
  }

  function isColumn(compPtr: CompRef | CompRef[]) {
    return (
      editorAPI.components.getType(compPtr) === constants.COMP_TYPES.COLUMN
    );
  }

  function isStrip(compPtr: CompRef | CompRef[]) {
    return (
      editorAPI.components.getType(compPtr) ===
      constants.COMP_TYPES.STRIP_COLUMNS_CONTAINER
    );
  }

  function setManagedColumn(columnPointer: CompRef, callback?: VoidFunction) {
    editorAPI.updateState({ managedColumnPointer: columnPointer }, callback);
  }

  function getManagedColumn() {
    const { managedColumnPointer } = editorAPI.store.getState();
    return managedColumnPointer &&
      editorAPI.components.is.exist(managedColumnPointer)
      ? managedColumnPointer
      : null;
  }

  function openManagePanel(
    columnsContainerPtr: CompRef,
    initialManagedColumn: CompRef | CompRef[],
  ) {
    if (Array.isArray(initialManagedColumn)) {
      initialManagedColumn = _.head(initialManagedColumn);
    }

    initialManagedColumn =
      initialManagedColumn ||
      editorAPI.components.getChildren(columnsContainerPtr)[0];

    editorAPI.columns.manage.setManagedColumn(
      initialManagedColumn,
      function () {
        editorAPI.selection.selectComponentByCompRef(columnsContainerPtr);
        editorAPI.panelManager.openComponentPanel(MANAGE_COLUMNS_PANEL_NAME, {
          selectedComponent: columnsContainerPtr,
        });
      },
    );
  }

  function closeManagePanel() {
    editorAPI.columns.manage.setManagedColumn(null);
    editorAPI.panelManager.closePanelByName(MANAGE_COLUMNS_PANEL_NAME);
  }

  return {
    isColumn,
    isStrip,
    isStripWithSingleColumn,
    isStripWithMultipleColumns,
    addColumn,
    duplicateColumn,
    removeColumn,
    getColumnIfStripIsSingleColumn,
    setInitialColumnChildrenLayouts,
    clearInitialColumnChildrenLayouts,
    canAdd,
    proportions: {
      set: setProportions,
      get: getProportions,
      flip: flipProportions,
      toPercentages: columnsUtils.toPercentages,
      toProportionsIntegers: columnsUtils.toProportionsIntegers,
    },
    columnAlignment: {
      set: setColumnAlignment,
      get: getColumnAlignment,
    },
    move: {
      left: moveLeft,
      right: moveRight,
    },
    manage: {
      setManagedColumn,
      getManagedColumn,
      openPanel: openManagePanel,
      closePanel: closeManagePanel,
    },
  };
}
